import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Button, Form, Input, InputNumber, Modal, Select, Switch, Tabs } from "antd";
import { useDispatch } from "react-redux";
import quickLinkService from "../../services/quick-link-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import UploadImage from "../../components/upload/image";
import fileUtils from "../../utils/file-utils";
import VisibilityFormItem from "../../components/input/visibility-form-item";
import { VISIBILITY_DEFAULT, groupType, fileTYPE_IMG } from "../../constants/app-constants";
import { FORM_LANG, formText, langOfTab } from "../../constants/form-i18n";
import { QUICK_LINK_ICONS, QUICK_LINK_ICON_COLOR } from "../../constants/quick-link-icons";
import { isUnsafeLink, withProtocol } from "../../utils/url-utils";

const { TabPane } = Tabs;

/**
 * NMU-608: add / edit one ทางลัดด่วน.
 *
 * A quick link is only ever an address — unlike a Slider or Intranet card there
 * is no เนื้อหา alternative, so there is no link *type* to choose and the URL
 * field is simply always required. An editor who wants to point at a CMS page
 * pastes that page's address, which is what the field's own validation accepts.
 */

/**
 * One row of the icon picker — the glyph beside its name.
 *
 * antd v4 builds the closed field's value out of the Option's children, so
 * drawing the icon here is what makes the chosen icon visible without the field
 * being opened (the pattern NMU-488 settled on for the intranet form).
 */
const IconOptionLabel = ({ option }) => {
  const Glyph = option.icon;
  return (
    <span className="d-inline-flex align-items-center">
      <Glyph size={20} color={QUICK_LINK_ICON_COLOR} className="me-2" />
      {option.label}
    </span>
  );
};

const AddEditQuickLinkDialog = ({ isShow, onCancel, onSaveComplete, editData, nextSeqNo }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (!isShow) return;
    if (editData) {
      form.setFieldsValue({
        titleTH: editData.title && editData.title.titleTH,
        titleEN: editData.title && editData.title.titleEN,
        icon: editData.icon || undefined,
        url: editData.url,
        seqNo: editData.seqNo,
        enableNewTab: !!editData.enableNewTab,
        status: !!editData.status,
        visibility: editData.visibility || VISIBILITY_DEFAULT,
      });
      setImage(editData.attachImage || null);
    } else {
      form.resetFields();
      // A new shortcut goes on the end rather than fighting for ลำดับ 1.
      form.setFieldsValue({ seqNo: nextSeqNo });
      setImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, editData]);

  const onUploadTemp = ({ file }) => {
    if (file.status === "done") {
      const { bean } = file.response;
      setImage(bean);
    }
  };

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      notifyUtitls.warning(t("warningTitle"), t("incompleteForm"));
      return;
    }

    // An icon *or* an emblem — with neither, the portal draws a generic chain
    // and every shortcut in the row looks the same.
    if (!image && !values.icon) {
      notifyUtitls.warning(t("warningTitle"), "กรุณาเลือกไอคอน หรืออัปโหลดรูปภาพ");
      return;
    }

    try {
      setLoading(true);
      dispatch(appLoaderAction.enable());

      const bean = {
        ...(editData ? JSON.parse(JSON.stringify(editData)) : {}),
        title: { titleTH: values.titleTH, titleEN: values.titleEN },
        icon: values.icon || null,
        attachImage: image,
        // NMU-602: saved with its https:// even if the field was never left.
        url: withProtocol(values.url),
        seqNo: values.seqNo === undefined || values.seqNo === null ? null : Number(values.seqNo),
        enableNewTab: !!values.enableNewTab,
        status: !!values.status,
        visibility: values.visibility,
      };

      if (editData) {
        await quickLinkService.edit(editData.quickLinkId, bean);
      } else {
        await quickLinkService.add(bean);
      }

      notifyUtitls.success(t("successTitle"), t("saveSuccess"));
      onSaveComplete();
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editData ? "แก้ไขทางลัดด่วน" : "เพิ่มทางลัดด่วน"}
      centered
      width={720}
      visible={isShow}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="submit" type="primary" onClick={onSave} loading={loading}>
          <Icon.Save className="me-2" size={17} />
          {t("save")}
        </Button>,
        <Button key="back" className="btn-gray" onClick={onCancel}>
          {t("cancel")}
        </Button>,
      ]}
    >
      <Form
        layout="vertical"
        form={form}
        preserve={false}
        initialValues={{ status: true, enableNewTab: true, visibility: VISIBILITY_DEFAULT }}
        validateMessages={{ required: t("required") }}
      >
        <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
          <TabPane tab={t("tabThai")} key="1" forceRender>
            <Form.Item label="ชื่อรายการ (ไทย)" name="titleTH" rules={[{ required: true }]}>
              {/* The portal clamps a long label to two lines — ร้องเรียนการ
                  ทุจริตและประพฤติมิชอบ is the ticket's own example. */}
              <Input placeholder="เช่น สายตรงอธิการบดี" />
            </Form.Item>
          </TabPane>
          <TabPane tab={t("tabEnglish")} key="2" forceRender>
            <Form.Item label="Title (EN)" name="titleEN">
              <Input />
            </Form.Item>
          </TabPane>
        </Tabs>

        <Form.Item label="ไอคอน" name="icon">
          <Select allowClear placeholder="เลือกไอคอน">
            {QUICK_LINK_ICONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                <IconOptionLabel option={opt} />
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* An uploaded emblem wins over the icon on the portal — it is how the
            ป.ป.ช. / ป.ป.ท. logos in the ticket get on screen at all, since no
            icon set contains them. */}
        <Form.Item label="หรืออัปโหลดรูปไอคอน (ใช้แทนไอคอนด้านบน)">
          <UploadImage
            listType="picture-card"
            accept={fileTYPE_IMG}
            showUploadList={false}
            onChange={onUploadTemp}
            className={image ? "view-upload" : null}
          >
            {image ? (
              <img
                src={
                  image.tempFilename
                    ? fileUtils.getTempFileRenderUrl(image.tempFilename, image.type)
                    : fileUtils.getFileRenderUrl(
                        groupType.QUICK_LINK,
                        editData ? editData.quickLinkId : null,
                        image.originalFilename,
                      )
                }
                alt="quick link"
                style={{ width: "100%" }}
              />
            ) : (
              t("uploadFile")
            )}
          </UploadImage>
        </Form.Item>

        {/*
          NMU-602: `egp.nmu.ac.th` becomes https://egp.nmu.ac.th as the editor
          leaves the field, so the address they see is the one saved. Typed
          as-is it was a path on the site, and a 404.
        */}
        <Form.Item
          label={t("link")}
          name="url"
          rules={[
            { required: true, whitespace: true },
            {
              validator: (_, value) =>
                isUnsafeLink(value)
                  ? Promise.reject(new Error("ลิงก์ต้องเป็นที่อยู่เว็บไซต์ เช่น https://egp.nmu.ac.th"))
                  : Promise.resolve(),
            },
          ]}
        >
          <Input
            placeholder="https://..."
            onBlur={(e) => {
              const formatted = withProtocol(e.target.value);
              if (formatted !== e.target.value) form.setFieldsValue({ url: formatted });
            }}
          />
        </Form.Item>

        <Form.Item label="ลำดับการแสดงผล" name="seqNo">
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <VisibilityFormItem lang={lang} />

        <Form.Item label={t("openInNewTab")} name="enableNewTab" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label={t("status")} name="status" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditQuickLinkDialog;
